import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../utils/apiUtils';

// ─── Banquest Hosted Tokenization v0.3 ──────────────────────────────────────
// Docs: https://docs.banquestgateway.com  (Hosted Tokenization section)
//
// Script creates secure iframes for card fields. On submit, getNonceToken()
// returns a one-time nonce which we POST to /banquest-charge. Card data never
// touches our server.

function loadBanquestTokenization() {
  return new Promise((resolve, reject) => {
    if (window.HostedTokenization) { resolve(window.HostedTokenization); return; }
    const script = document.createElement('script');
    // Production: https://tokenization.banquestgateway.com/tokenization/v0.3
    // Sandbox:    https://tokenization.sandbox.banquestgateway.com/tokenization/v0.3
    const base = import.meta.env.VITE_BANQUEST_SANDBOX === 'true'
      ? 'https://tokenization.sandbox.banquestgateway.com'
      : 'https://tokenization.banquestgateway.com';
    script.src = `${base}/tokenization/v0.3`;
    script.async = true;
    script.onload = () => {
      const check = setInterval(() => {
        if (window.HostedTokenization) { clearInterval(check); resolve(window.HostedTokenization); }
      }, 50);
      setTimeout(() => { clearInterval(check); reject(new Error('Payment library did not initialize')); }, 8000);
    };
    script.onerror = () => reject(new Error('Failed to load payment form. Please refresh and try again.'));
    document.head.appendChild(script);
  });
}

export default function WaitlistCheckout() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const reg = searchParams.get('reg');
  const lockedRoute = searchParams.get('route');  // Route from registration — locks checkout to this route

  // loading | chooseFlight | chooseRoute | redirecting | paymentForm | processing | success | alreadyPaid | soldOut | error
  const [state, setState] = useState('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [personName, setPersonName] = useState('');
  const [flightLabel, setFlightLabel] = useState('');
  const [flightOptions, setFlightOptions] = useState([]);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [routeOptions, setRouteOptions] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [confirming, setConfirming] = useState(false);

  // Banquest-specific state
  const [paymentProvider, setPaymentProvider] = useState('stripe');
  const [paymentAmount, setPaymentAmount] = useState(null);
  const [registrationId, setRegistrationId] = useState(null);
  const [registrationCode, setRegistrationCode] = useState('');
  const [tokenizationReady, setTokenizationReady] = useState(false);
  const [cardError, setCardError] = useState('');
  const [paymentResult, setPaymentResult] = useState(null);
  const hostedTokenizationRef = useRef(null);
  const initStartedRef = useRef(false);

  // ─── Initial load: verify code ───
  useEffect(() => {
    if (!code || !reg) {
      setState('error');
      setErrorMsg('Invalid link — missing code or registration ID.');
      return;
    }

    const routeParam = lockedRoute ? `&route=${encodeURIComponent(lockedRoute)}` : '';
    apiFetch(`${import.meta.env.VITE_API_URL}/flight/checkout?code=${encodeURIComponent(code)}&reg=${encodeURIComponent(reg)}${routeParam}`)
      .then(data => {
        if (data.paymentProvider) setPaymentProvider(data.paymentProvider);

        if (data.status === 'success') {
          if (data.alreadyPaid) { setState('alreadyPaid'); return; }
          setPersonName(`${data.firstName} ${data.lastName}`);
          if (data.flightLabel) setFlightLabel(data.flightLabel);

          if (data.paymentProvider === 'banquest') {
            setPaymentAmount(data.amount);
            setRegistrationId(data.registrationId || reg);
            setRegistrationCode(data.registrationCode || '');
            setState('paymentForm');
          } else {
            // Stripe: redirect
            setState('redirecting');
            sessionStorage.setItem('pendingRegistration', JSON.stringify({
              registrationCode: data.registrationCode || '', id: reg,
              route: data.route || '', flightId: data.flightId || ''
            }));
            setTimeout(() => { window.location.href = data.stripeUrl; }, 2000);
          }
        } else if (data.status === 'choose_flight') {
          setPersonName(`${data.firstName} ${data.lastName}`);
          setFlightOptions(data.flightOptions || []);
          setState('chooseFlight');
        } else if (data.status === 'choose_route') {
          setPersonName(`${data.firstName} ${data.lastName}`);
          if (data.flightLabel) setFlightLabel(data.flightLabel);
          setRouteOptions(data.routeOptions || []);
          setState('chooseRoute');
        } else if (data.status === 'registered') {
          setState('registered');
        } else if (data.status === 'sold_out' || data.status === 'route_sold_out') {
          setState('soldOut');
          setErrorMsg(data.message);
        } else {
          setState('error');
          setErrorMsg(data.message || 'Something went wrong.');
        }
      })
      .catch(err => { setState('error'); setErrorMsg(err.message || 'Network error. Please try again.'); });
  }, [code, reg]);

  // ─── Initialize Banquest Hosted Tokenization when entering paymentForm ───
  useEffect(() => {
    if (state !== 'paymentForm' || initStartedRef.current) return;
    initStartedRef.current = true;

    const tokenizationKey = import.meta.env.VITE_BANQUEST_TOKENIZATION_KEY;
    if (!tokenizationKey) {
      setCardError('Payment system not configured. Please contact flights@igudys.com.');
      return;
    }

    loadBanquestTokenization()
      .then(HostedTokenization => {
        const fieldStyle = 'border: 1px solid #d1d5db; border-radius: 8px; padding: 10px 12px; font-size: 16px; height: 44px; box-sizing: border-box; outline: none;';
        const instance = new HostedTokenization(tokenizationKey, {
          target: '#banquest-card-form',
          showZip: true,
          requireCvv2: true,
          styles: {
            container: 'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 16px;',
            card: fieldStyle + ' width: 100%;',
            expiryMonth: fieldStyle + ' width: 70px; text-align: center;',
            expirySeparator: 'font-size: 20px; color: #9ca3af; padding: 0 4px; line-height: 44px;',
            expiryYear: fieldStyle + ' width: 70px; text-align: center;',
            cvv2: fieldStyle + ' width: 80px; text-align: center;',
            avsZip: fieldStyle + ' width: 140px;',
            labelType: 'static-top',
            labels: 'font-size: 14px; font-weight: 500; color: #374151; margin-bottom: 6px; display: block;',
          },
        });

        instance.on('ready', () => {
          setTokenizationReady(true);
        });

        instance.on('input', ({ error }) => {
          if (error) setCardError(error.message || 'Please check your card details');
          else setCardError('');
        });

        hostedTokenizationRef.current = instance;
      })
      .catch(err => setCardError(err.message));

    return () => {
      if (hostedTokenizationRef.current) {
        try { hostedTokenizationRef.current.destroy(); } catch (_) {}
        hostedTokenizationRef.current = null;
      }
    };
  }, [state]);

  // ─── Flight confirmation handler (multi-flight codes) ───
  const handleConfirmFlight = async () => {
    if (!selectedFlight || confirming) return;
    setConfirming(true);

    try {
      const data = await apiFetch(
        `${import.meta.env.VITE_API_URL}/flight/checkout?code=${encodeURIComponent(code)}&reg=${encodeURIComponent(reg)}&flight=${encodeURIComponent(selectedFlight)}`
      );

      if (data.status === 'choose_route') {
        setPersonName(`${data.firstName} ${data.lastName}`);
        if (data.flightLabel) setFlightLabel(data.flightLabel);
        if (data.paymentProvider) setPaymentProvider(data.paymentProvider);
        setRouteOptions(data.routeOptions || []);
        setSelectedRoute(null);
        setState('chooseRoute');
      } else if (data.status === 'registered') {
        setState('registered');
      } else if (data.status === 'sold_out') {
        setState('soldOut');
        setErrorMsg(data.message);
      } else {
        setState('error');
        setErrorMsg(data.message || 'Something went wrong.');
      }
    } catch (err) {
      setState('error');
      setErrorMsg(err.message || 'Network error. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  // ─── Route confirmation handler ───
  const handleConfirmRoute = async () => {
    if (!selectedRoute || confirming) return;
    setConfirming(true);

    try {
      const flightParam = selectedFlight ? `&flight=${encodeURIComponent(selectedFlight)}` : '';
      const data = await apiFetch(
        `${import.meta.env.VITE_API_URL}/flight/checkout?code=${encodeURIComponent(code)}&reg=${encodeURIComponent(reg)}&route=${encodeURIComponent(selectedRoute)}${flightParam}`
      );

      if (data.paymentProvider) setPaymentProvider(data.paymentProvider);

      if (data.status === 'success') {
        if (data.paymentProvider === 'banquest') {
          setPersonName(`${data.firstName} ${data.lastName}`);
          setPaymentAmount(data.amount);
          setRegistrationId(data.registrationId || reg);
          setRegistrationCode(data.registrationCode || '');
          setState('paymentForm');
        } else if (data.stripeUrl) {
          setState('redirecting');
          sessionStorage.setItem('pendingRegistration', JSON.stringify({
            registrationCode: data.registrationCode || '', id: reg,
            route: data.route || '', flightId: data.flightId || ''
          }));
          setTimeout(() => { window.location.href = data.stripeUrl; }, 1500);
        } else {
          setState('error');
          setErrorMsg('No payment URL returned.');
        }
      } else if (data.status === 'registered') {
        setState('registered');
      } else if (data.status === 'sold_out' || data.status === 'route_sold_out') {
        setState('soldOut');
        setErrorMsg(data.message);
      } else {
        setState('error');
        setErrorMsg(data.message || 'Something went wrong.');
      }
    } catch (err) {
      setState('error');
      setErrorMsg(err.message || 'Network error. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  // ─── Banquest payment submit ───
  const handleBanquestPay = useCallback(async () => {
    if (!tokenizationReady || !hostedTokenizationRef.current || state === 'processing') return;
    setCardError('');

    let result;
    try {
      // getNonceToken() returns: { nonce, maskedCard, cardType, last4, expiryMonth, expiryYear }
      result = await hostedTokenizationRef.current.getNonceToken();
    } catch (err) {
      setCardError(err?.message || 'Please check your card details and try again.');
      return;
    }

    if (!result?.nonce) {
      setCardError('Could not process card. Please check your details and try again.');
      return;
    }

    setState('processing');

    try {
      const chargeResponse = await apiFetch(`${import.meta.env.VITE_API_URL}/banquest-charge`, {
        method: 'POST',
        body: JSON.stringify({
          nonce: result.nonce,
          expiryMonth: result.expiryMonth,
          expiryYear: result.expiryYear,
          avsZip: result.avsZip || undefined,
          registrationId,
          code,
        }),
      });

      if (chargeResponse.status === 'success') {
        setPaymentResult(chargeResponse);
        setRegistrationCode(chargeResponse.registrationCode || registrationCode);
        setState('success');
      } else if (chargeResponse.status === 'already_paid') {
        setState('alreadyPaid');
      } else if (chargeResponse.status === 'sold_out') {
        setState('soldOut');
        setErrorMsg(chargeResponse.message || 'All spots have been filled. Your card was NOT charged.');
      } else if (chargeResponse.status === 'declined') {
        setCardError(chargeResponse.message || 'Payment declined. Please try a different card.');
        setState('paymentForm');
      } else {
        setCardError(chargeResponse.message || 'Payment failed. Please try again.');
        setState('paymentForm');
      }
    } catch (err) {
      setCardError(err.message || 'Network error. Please try again.');
      setState('paymentForm');
    }
  }, [tokenizationReady, state, registrationId, code, registrationCode]);

  // ─── Render ───
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-blue-700 px-6 py-5 text-center">
          <div className="flex items-center justify-center gap-4 mb-3">
            <img src="/igud-logo.png" alt="Igud Yeshivos" className="h-10 w-auto" />
            <img src="/ehk-logo_2-1.png" alt="Eretz Hakodesh" className="h-10 w-auto" />
          </div>
          <h1 className="text-white font-bold text-xl">Charter Flight Booking</h1>
        </div>

        <div className="px-6 py-8">
          {state === 'loading' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Verifying your booking link…</p>
            </div>
          )}

          {state === 'chooseFlight' && (
            <div>
              <div className="text-center mb-6">
                <div className="text-4xl mb-3">✈️</div>
                <h2 className="text-xl font-bold text-gray-800 mb-1">Choose Your Flight</h2>
                {personName && <p className="text-gray-500 text-sm mt-1">Booking for {personName}</p>}
              </div>

              <div className="space-y-3 mb-6">
                {flightOptions.map(opt => (
                  <button
                    key={opt.flightId}
                    onClick={() => setSelectedFlight(opt.flightId)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      selectedFlight === opt.flightId
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <p className="font-semibold text-gray-800">{opt.label}</p>
                    <div className="mt-2 flex flex-wrap gap-x-4 text-sm text-gray-600">
                      {opt.routes.map(r => (
                        <span key={r.route}>
                          {r.label}: <strong>${r.price.toLocaleString()}</strong>
                        </span>
                      ))}
                    </div>
                    {/* spots remaining hidden */}
                  </button>
                ))}
              </div>

              <button
                onClick={handleConfirmFlight}
                disabled={!selectedFlight || confirming}
                className={`w-full py-4 rounded-lg font-bold text-lg transition-all ${
                  selectedFlight && !confirming
                    ? 'bg-green-600 hover:bg-green-700 text-white cursor-pointer'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {confirming ? 'Loading…' : 'Continue →'}
              </button>

              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mt-5 text-sm text-blue-800">
                <strong>Please note:</strong> Seats are limited and available first-come, first-served.
                Your spot is only confirmed once your payment is completed.
              </div>
            </div>
          )}

          {state === 'chooseRoute' && (
            <div>
              <div className="text-center mb-6">
                <div className="text-4xl mb-3">✈️</div>
                <h2 className="text-xl font-bold text-gray-800 mb-1">Choose Your Route</h2>
                {flightLabel && (
                  <p className="text-blue-700 font-semibold text-sm">{flightLabel}</p>
                )}
                {personName && <p className="text-gray-500 text-sm mt-1">Booking for {personName}</p>}
              </div>

              <div className="space-y-3 mb-6">
                {routeOptions.map(opt => (
                  <button
                    key={opt.route}
                    onClick={() => setSelectedRoute(opt.route)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      selectedRoute === opt.route
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-gray-800">
                          {opt.route === 'jerusalem-milan' ? 'Milan Only' : 'Milan + New York'}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">{opt.label}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg text-gray-800">${opt.price.toLocaleString()}</p>
                        {/* spots remaining hidden */}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <button
                onClick={handleConfirmRoute}
                disabled={!selectedRoute || confirming}
                className={`w-full py-4 rounded-lg font-bold text-lg transition-all ${
                  selectedRoute && !confirming
                    ? 'bg-green-600 hover:bg-green-700 text-white cursor-pointer'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {confirming ? 'Processing…' : 'Continue to Payment →'}
              </button>

              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mt-5 text-sm text-blue-800">
                <strong>Please note:</strong> Seats are limited and available first-come, first-served.
                Your spot is only confirmed once your payment is completed. Please complete payment promptly
                to secure your seat.
              </div>
            </div>
          )}

          {/* ─── Banquest Card Payment Form ─── */}
          {state === 'paymentForm' && (
            <div>
              <div className="text-center mb-6">
                <div className="text-4xl mb-3">💳</div>
                <h2 className="text-xl font-bold text-gray-800 mb-1">Enter Payment Details</h2>
                {flightLabel && <p className="text-blue-700 font-semibold text-sm">{flightLabel}</p>}
                {personName && <p className="text-gray-500 text-sm mt-1">Booking for {personName}</p>}
                {paymentAmount && (
                  <p className="text-2xl font-bold text-gray-800 mt-3">${paymentAmount.toLocaleString()}</p>
                )}
              </div>

              {/* Banquest Hosted Tokenization renders its iframes into this div */}
              <style>{`
                #banquest-card-form iframe {
                  width: 100% !important;
                  min-height: 380px !important;
                  border: none !important;
                }
              `}</style>
              <div id="banquest-card-form" className="mb-6"></div>

              {cardError && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm text-red-700">
                  {cardError}
                </div>
              )}

              <button
                onClick={handleBanquestPay}
                disabled={!tokenizationReady}
                className={`w-full py-4 rounded-lg font-bold text-lg transition-all ${
                  tokenizationReady
                    ? 'bg-green-600 hover:bg-green-700 text-white cursor-pointer'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {tokenizationReady ? `Pay $${paymentAmount?.toLocaleString() || ''}` : 'Loading payment form…'}
              </button>

              <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mt-4 text-xs text-gray-500 text-center">
                <p className="mb-1">The charge on your statement will appear from:</p>
                <p className="font-semibold text-gray-700">THE COORDINATING COUNCIL FOR JEWISH HOMELAND (CCJH)</p>
                <p className="mt-1">EIN 26-1320314 · 501(c)(3) tax-exempt organization</p>
              </div>

              <div className="flex items-center justify-center gap-2 mt-3 text-xs text-gray-400">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>Secure payment — your card details are encrypted</span>
              </div>
            </div>
          )}

          {/* ─── Processing ─── */}
          {state === 'processing' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto mb-4"></div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Processing payment…</h2>
              <p className="text-gray-500 text-sm">Please do not close this page.</p>
            </div>
          )}

          {/* ─── Payment Success (Banquest — inline, no redirect) ─── */}
          {state === 'success' && (
            <div className="text-center">
              <div className="text-4xl mb-4">🎉</div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Payment Received!</h2>
              <p className="text-gray-600 mb-4">
                Thank you! You will receive a confirmation email shortly.
              </p>

              {registrationCode && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-green-700 mb-1">Your Registration Code:</p>
                  <p className="text-xl font-mono font-bold text-green-900">{registrationCode}</p>
                </div>
              )}

              {paymentResult?.last4 && (
                <p className="text-sm text-gray-500">
                  Charged to {paymentResult.cardType || 'card'} ending in {paymentResult.last4}
                </p>
              )}

              <p className="text-gray-500 text-sm mt-4">
                Questions? <a href="mailto:flights@igudys.com" className="text-blue-600 underline">flights@igudys.com</a>
              </p>
            </div>
          )}

          {state === 'redirecting' && (
            <div className="text-center">
              <div className="text-4xl mb-4">✅</div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Taking you to payment…</h2>
              {personName && <p className="text-gray-600 mb-1">Welcome, {personName}</p>}
              <p className="text-gray-500 text-sm mb-6">Redirecting you to the payment page…</p>
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto"></div>
            </div>
          )}

          {state === 'alreadyPaid' && (
            <div className="text-center">
              <div className="text-4xl mb-4">🎉</div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">You're all set!</h2>
              <p className="text-gray-600">Your payment has already been received. You'll receive confirmation details by email.</p>
              <p className="text-gray-500 text-sm mt-4">Questions? <a href="mailto:flights@igudys.com" className="text-blue-600 underline">flights@igudys.com</a></p>
            </div>
          )}

          {state === 'registered' && (
            <div className="text-center">
              <div className="text-4xl mb-4">&#x2705;</div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Registration Received</h2>
              <p className="text-gray-600 mb-4">
                Thank you for your interest. All three flights have departed successfully. Your registration has been received and we are gauging interest for an additional flight. We will email you with details as they become available.
              </p>
              <p className="text-gray-500 text-sm">
                Questions? <a href="mailto:flights@igudys.com" className="text-blue-600 underline">flights@igudys.com</a>
              </p>
            </div>
          )}

          {state === 'soldOut' && (
            <div className="text-center">
              <div className="text-4xl mb-4">&#x1F614;</div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">All Spots Filled</h2>
              <p className="text-gray-600 mb-4">{errorMsg}</p>
              <p className="text-gray-500 text-sm">
                Contact us at{' '}
                <a href="mailto:flights@igudys.com" className="text-blue-600 underline">flights@igudys.com</a>
              </p>
            </div>
          )}

          {state === 'error' && (
            <div className="text-center">
              <div className="text-4xl mb-4">⚠️</div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Link Not Valid</h2>
              <p className="text-gray-600 mb-4">{errorMsg}</p>
              <p className="text-gray-500 text-sm">
                If you believe this is a mistake, please contact us at{' '}
                <a href="mailto:flights@igudys.com" className="text-blue-600 underline">flights@igudys.com</a>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
